import { PlayerManager } from 'chromecast-caf-receiver/cast.framework';
import { EventType } from 'chromecast-caf-receiver/cast.framework.events';
import { Logger } from '../logger';
import { signalRenditionChange } from '../monitoring/mux';

/**
 * Rendition reporter — reports the active video rendition (resolution/bitrate)
 * to Mux Data so the "Video Width/Height" and "Bitrate" fields stop arriving
 * "unset" in the dashboard.
 *
 * **Context (P1 · Code 100 / shaka 3014).** The `@mux/mux-data-chromecast` SDK
 * emits `renditionchange` on `BITRATE_CHANGED`, but on the Cast path only sends
 * `video_source_bitrate` — never the width/height. Without those fields we can't
 * confirm the 4K-selection hypothesis behind the shaka 3014 MSE failures. This
 * module fills the gap.
 *
 * **Source of truth.** The embedded Shaka player is not reachable from the
 * receiver; CAF wraps it. `playerManager.getStats()` is the CAF-native
 * equivalent, exposing the current video track `width`/`height` and
 * `streamBandwidth` (total, in bit/sec — the exact unit Mux expects). Video
 * codec is not exposed by any CAF surface, so it is omitted (best-effort).
 */

const LOG_TAG = 'RENDITION';

/**
 * Reads the current rendition from CAF player stats and forwards it to Mux.
 * `signalRenditionChange` no-ops when the stats have no active video track yet.
 */
function reportCurrentRendition(playerManager: PlayerManager): void {
  const stats = playerManager.getStats();

  signalRenditionChange({
    width: stats.width,
    height: stats.height,
    bitrate: stats.streamBandwidth,
  });
}

/**
 * Registers the rendition reporter on the given player manager.
 *
 * Wires up:
 * - `BITRATE_CHANGED` → report the new rendition (primary ABR-switch signal)
 * - `PLAYER_LOAD_COMPLETE` → report the initial rendition once the manifest is
 *   parsed (stats may still be empty at this instant; the first
 *   `BITRATE_CHANGED` then corrects it)
 *
 * @param playerManager - Cast player manager instance
 */
export function registerRenditionReporter(playerManager: PlayerManager): void {
  playerManager.addEventListener(EventType.BITRATE_CHANGED, () => {
    reportCurrentRendition(playerManager);
  });

  playerManager.addEventListener(EventType.PLAYER_LOAD_COMPLETE, () => {
    reportCurrentRendition(playerManager);
  });

  Logger.info(LOG_TAG, 'Rendition reporter registered');
}
